/**
 * Pure-function conflict resolver for cloud-sync manifests.
 *
 * Mirrors `kaether_for_ios/Stoat/E2EE/CloudSyncConflictPolicy.swift` —
 * deterministic, no-IO, no-signature-verification (the caller verifies
 * signatures BEFORE handing the pair to this function). The job is to pick
 * a winner and return a human-readable reason so the audit log can show
 * why each side was chosen.
 *
 * STRATEGY (strict, deterministic):
 *
 *   1. Strictly newer `itemVersion` wins.
 *   2. Tie on itemVersion → strictly newer `ts` wins.
 *   3. Tie on itemVersion AND ts → deterministic tiebreak by
 *      `authorDeviceId ascending` (lower deviceId wins).
 *
 * The third tier is purely deterministic so two devices running this
 * function on the same pair always pick the same winner without any
 * shared clock or coordination — required because the conflict can be
 * observed independently on both sides (each device pulls the remote and
 * compares against its own local).
 *
 * THREAT MODEL
 *   The resolver is a pure decision. It does NOT:
 *     - verify signatures (caller's job — `openItem` does this)
 *     - hash blobs (caller's job)
 *     - touch storage (caller's job — `CloudSyncStore` does this)
 *   This separation means a single failure-injection test can target one
 *   layer without exercising the others.
 *
 * Service-Worker importability guarantee:
 *   This file uses NO `window`, `document`, or DOM-only APIs. Pure
 *   numeric comparisons.
 *
 * @module CloudSyncConflictPolicy
 */

import type { CloudSyncManifest } from "./CloudSyncRecord";

/** Discrete reasons the resolver returns. The audit log displays these. */
export type CloudSyncConflictReason =
  | "local-itemVersion-strictly-newer"
  | "remote-itemVersion-strictly-newer"
  | "local-ts-strictly-newer"
  | "remote-ts-strictly-newer"
  | "local-authorDeviceId-tiebreak"
  | "remote-authorDeviceId-tiebreak"
  | "identical-manifests";

/** Returned to the caller — a winner and the reason it won. */
export interface CloudSyncConflictResult {
  readonly winner: "local" | "remote";
  readonly reason: CloudSyncConflictReason;
}

/**
 * Resolve the local-vs-remote conflict per the documented strategy.
 *
 * @param local   Local manifest.
 * @param remote  Remote manifest.
 * @returns       `{ winner, reason }`. If the two manifests have identical
 *                `(itemVersion, ts, authorDeviceId)`, returns
 *                `{ winner: 'local', reason: 'identical-manifests' }` — the
 *                local copy is canonical because we're already storing it.
 */
export function resolveConflict(
  local: CloudSyncManifest,
  remote: CloudSyncManifest,
): CloudSyncConflictResult {
  // 1. itemVersion (strict).
  if (local.itemVersion > remote.itemVersion) {
    return {
      winner: "local",
      reason: "local-itemVersion-strictly-newer",
    };
  }
  if (remote.itemVersion > local.itemVersion) {
    return {
      winner: "remote",
      reason: "remote-itemVersion-strictly-newer",
    };
  }

  // 2. ts (strict).
  if (local.ts > remote.ts) {
    return { winner: "local", reason: "local-ts-strictly-newer" };
  }
  if (remote.ts > local.ts) {
    return { winner: "remote", reason: "remote-ts-strictly-newer" };
  }

  // 3. authorDeviceId ascending tiebreak — strictly deterministic so two
  //    devices comparing the same pair never disagree.
  if (local.authorDeviceId < remote.authorDeviceId) {
    return {
      winner: "local",
      reason: "local-authorDeviceId-tiebreak",
    };
  }
  if (remote.authorDeviceId < local.authorDeviceId) {
    return {
      winner: "remote",
      reason: "remote-authorDeviceId-tiebreak",
    };
  }

  // Identical (itemVersion, ts, authorDeviceId). The local copy is what
  // we already have on disk; keeping it is a no-op.
  return { winner: "local", reason: "identical-manifests" };
}
